import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

// Account page: the switches for the emails.
// "Email me about songs" covers new songs, featured songs, the monthly roundup and the come-back emails. Turning it off puts the address
// on the do-not-email list, and that stops the DJ Booth emails too.
// "Email me about the DJ Booth" (Premium members) stops only the weekly DJ Booth introduction and leaves the song emails alone.
function Row({ id, label, text, on, busy, onFlip, testid, disabled, hint }) {
  return (
    <div>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div id={id} className="font-medium">{label}</div>
          <p className="mt-1 text-xs text-zinc-500">{text}</p>
        </div>
        <button type="button" role="switch" aria-checked={on} aria-labelledby={id} onClick={onFlip} disabled={busy || disabled} data-testid={`${testid}-switch`}
          className={`shrink-0 relative w-12 h-7 rounded-full border transition-colors disabled:opacity-60 ${on ? "bg-cyan-400 border-cyan-300" : "bg-white/10 border-white/20"}`}>
          <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
        </button>
      </div>
      <div className="mt-2 text-xs text-zinc-400" data-testid={`${testid}-state`}>{on ? "On" : "Off"}</div>
      {hint && <p className="mt-1 text-xs text-zinc-500" data-testid={`${testid}-hint`}>{hint}</p>}
    </div>
  );
}

export default function MailPreference() {
  const [state, setState] = useState(null);     // null until the answer comes back: { new_songs, dj, premium }
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/me/mail").then((r) => { if (alive) setState({ new_songs: !!r.data.new_songs, dj: r.data.dj !== false, premium: !!r.data.premium }); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (state === null) return null;

  const save = async (key, value, okOn, okOff) => {
    setBusy(key);
    try {
      const r = await api.put("/me/mail", { [key]: value });
      setState({ new_songs: !!r.data.new_songs, dj: r.data.dj !== false, premium: !!r.data.premium });
      toast.success(value ? okOn : okOff);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save. Try again."); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="mail-preference" className="min-w-0 bg-zinc-900/60 border border-white/10 rounded-2xl p-6">
      <div className="font-mono-ui text-xs uppercase tracking-widest text-zinc-500 mb-2">Emails</div>
      <Row id="mail-pref-label" testid="mail-preference" label="Email me about songs"
        text="New songs, featured songs, a monthly roundup, and a note about what you missed if you have been away a while."
        on={state.new_songs} busy={busy === "new_songs"} onFlip={() => save("new_songs", !state.new_songs, "You will get emails about songs", "No more emails about songs")} />
      {state.premium && (
        <div className="mt-5 pt-5 border-t border-white/10">
          <Row id="mail-pref-dj-label" testid="mail-preference-dj" label="Email me about the DJ Booth"
            text="A few weekly emails that show you the DJ Booth. They stop on their own when you try it."
            on={state.new_songs && state.dj} busy={busy === "dj"} disabled={!state.new_songs}
            hint={!state.new_songs ? "Turn on emails about songs first. That switch stops all of these emails." : undefined}
            onFlip={() => save("dj", !state.dj, "You will get the DJ Booth emails", "No more DJ Booth emails")} />
        </div>
      )}
      <p className="mt-4 text-xs text-zinc-500">Emails about your account, like password resets and billing notices, always go out.</p>
    </div>
  );
}
